import React from 'react';
import { Box, MenuItem, Stack, Typography, TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';

import { FeeStyleToggle } from './FeeStyleToggle';
import { MAX_DESCRIPTION_LENGTH, MAX_VALUE_DIGITS } from '../constants';
import { AcceptedCurrency, FeeValue, isFeeWithValue } from '../../../store/modules/merchant/types';

/** Width of fee value + description fields; keep conflict alerts aligned with the description input. */
export const FEE_FORM_MAX_WIDTH = '520px';

type Props = {
  feeType: FeeValue['feeType'];
  localFeeValue: Partial<FeeValue>;
  setLocalFeeValue: React.Dispatch<React.SetStateAction<Partial<FeeValue>>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  error: boolean;
  disabled?: boolean;
  currency?: AcceptedCurrency;
};

export const FeeForm = ({
  feeType,
  localFeeValue,
  setLocalFeeValue,
  setError,
  error,
  disabled = false,
  currency,
}: Props) => {
  const currencyCode = currency?.code || 'USD';
  return (
    <Stack spacing={2} sx={{ mt: 2, maxWidth: FEE_FORM_MAX_WIDTH }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ pt: 0.5 }}>
          <FeeStyleToggle
            value={localFeeValue?.style ?? 'PERCENTAGE'}
            onChange={(newValue) => {
              setLocalFeeValue((prev) => ({ ...prev, style: newValue }));
              setError(
                isFeeWithValue(localFeeValue) && localFeeValue.value !== 0 && !localFeeValue.value,
              );
            }}
            disabled={disabled}
          />
        </Box>
        {localFeeValue?.style !== 'FLAT' && (
          <NumericFormat
            customInput={TextField}
            variant="outlined"
            label={
              feeType === 'SURCHARGE_FEE' ? 'Surcharge Fee Percentage' : 'General Fee Percentage'
            }
            suffix="%"
            fixedDecimalScale
            decimalScale={2}
            allowNegative={false}
            onKeyDown={(event) => {
              const target = event.target as HTMLInputElement;
              if (
                target.value.length > MAX_VALUE_DIGITS &&
                event.key !== 'Backspace' &&
                event.key !== 'Delete'
              ) {
                event.preventDefault();
              }
            }}
            onChange={(event) => {
              const target = event.target as HTMLInputElement;
              const value = parseFloat(target.value.substring(0, target.value.length - 1));
              setLocalFeeValue((prev) => ({ ...prev, value }));
              setError(value !== 0 && !value);
            }}
            value={isFeeWithValue(localFeeValue) ? localFeeValue.value : ''}
            disabled={disabled}
            error={error}
            helperText={error && 'Percentage is required'}
          />
        )}
        {localFeeValue?.style === 'FLAT' && (
          <Stack direction="row" spacing={1}>
            <NumericFormat
              customInput={TextField}
              variant="outlined"
              label={
                feeType === 'SURCHARGE_FEE'
                  ? 'Surcharge Fee Dollar Amount'
                  : 'General Fee Dollar Amount'
              }
              prefix={currency?.symbol || '$'}
              fixedDecimalScale
              decimalScale={2}
              allowNegative={false}
              onKeyDown={(event) => {
                const target = event.target as HTMLInputElement;
                if (
                  target.value.length > MAX_VALUE_DIGITS &&
                  event.key !== 'Backspace' &&
                  event.key !== 'Delete'
                ) {
                  event.preventDefault();
                }
              }}
              onChange={(event) => {
                const target = event.target as HTMLInputElement;
                const value = parseFloat(target.value.substring(1)) * 100;
                setLocalFeeValue((prev) => ({ ...prev, value }));
                setError(value !== 0 && !value);
              }}
              value={isFeeWithValue(localFeeValue) ? localFeeValue.value / 100 : undefined}
              disabled={disabled}
              error={error}
              helperText={error && 'Dollar amount is required'}
            />
            <TextField
              select
              label="Currency"
              defaultValue={currencyCode}
              sx={{
                maxWidth: '80px',
                '& .MuiSelect-select': {
                  height: '23px',
                  width: '93px',
                  py: '12.5px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px',
                },
              }}
              onChange={(event) => {
                const newCurrencyCode = event.target.value;
                setLocalFeeValue((prev) => ({ ...prev, currencyCode: newCurrencyCode }));
              }}
              disabled={disabled}
            >
              <MenuItem key={currencyCode} value={currencyCode}>
                {currencyCode}
              </MenuItem>
            </TextField>
          </Stack>
        )}
      </Stack>
      <Box sx={{ textAlign: 'right' }}>
        <TextField
          fullWidth
          label={
            feeType === 'SURCHARGE_FEE' ? 'Surcharge Fee Description' : 'General Fee Description'
          }
          value={localFeeValue?.description ?? ''}
          onChange={(event) => {
            const description = event.target.value;
            if (description.length > MAX_DESCRIPTION_LENGTH) {
              return;
            }
            setLocalFeeValue((prev) => ({ ...prev, description }));
          }}
          disabled={disabled}
        />
        <Typography
          sx={{
            mt: '8px',
            fontSize: '10px',
            color: 'grey.600',
          }}
        >
          {localFeeValue?.description?.length || 0}/{MAX_DESCRIPTION_LENGTH} characters
        </Typography>
      </Box>
    </Stack>
  );
};
